import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PRODUCTS } from '../data/mock';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Truck, ShieldCheck, RotateCcw, Minus, Plus, ChevronRight, CheckCircle2 } from 'lucide-react';
import ProductCard from '../components/ProductCard';

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = PRODUCTS.find((p) => p.id === slug);
  const { addItem } = useCart();
  const { toast } = useToast();
  const [qty, setQty] = useState(1);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-slate-900">Product not found</h1>
        <Button asChild className="mt-6 bg-[#5d728c] hover:bg-[#4d6278]">
          <Link to="/shop-all">Back to Shop</Link>
        </Button>
      </div>
    );
  }

  const related = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAdd = () => {
    addItem(product, qty);
    toast({ title: 'Added to cart', description: `${product.name} × ${qty}` });
  };

  const handleBuy = () => {
    addItem(product, qty);
    navigate('/checkout');
  };

  return (
    <div className="bg-white">
      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <nav className="text-sm text-slate-500 flex items-center gap-1 flex-wrap">
          <Link to="/" className="hover:text-[#5d728c]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/shop-all" className="hover:text-[#5d728c]">Shop</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-700">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-2 gap-12">
        <div className="bg-slate-50 rounded-xl p-8 flex items-center justify-center">
          <img src={product.image} alt={product.name} className="max-h-[520px] w-full object-contain" />
        </div>
        <div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900">{product.name}</h1>
          <p className="mt-3 text-3xl font-semibold text-slate-800">${product.price.toFixed(2)}</p>
          <p className="text-sm text-emerald-700 mt-1">In stock — Ships within 1 business day</p>
          <p className="mt-6 text-slate-600 leading-relaxed">{product.description}</p>

          {product.features?.length > 0 && (
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-700 text-sm">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#5d728c] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-slate-300 rounded-full">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-3 py-2 text-slate-600 hover:text-[#5d728c]"
                aria-label="Decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-4 text-slate-800 font-medium min-w-[2ch] text-center">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                className="px-3 py-2 text-slate-600 hover:text-[#5d728c]"
                aria-label="Increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <Button onClick={handleAdd} className="bg-[#5d728c] hover:bg-[#4d6278] text-white rounded-full px-7">
              Add to Cart
            </Button>
            <Button onClick={handleBuy} variant="outline" className="border-slate-300 rounded-full px-7">
              Buy Now
            </Button>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-200 pt-6">
            <div className="flex items-center gap-2 text-sm text-slate-700"><Truck className="w-5 h-5 text-[#5d728c]" /> Free USPS Shipping</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><RotateCcw className="w-5 h-5 text-[#5d728c]" /> 30-Day Returns</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><ShieldCheck className="w-5 h-5 text-[#5d728c]" /> Secure Checkout</div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-serif text-2xl md:text-3xl text-slate-900 mb-6">You may also like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
